/**
 * operators.ts — Pure operator evaluation matrix.
 *
 * applyOperator(operator, lhs, rhs): boolean
 *
 * Rules (spec :159-167):
 *   - Numeric (gt/gte/lt/lte): empty value (missing answer / URL param /
 *       variable) after trim() on EITHER side → FALSE, checked BEFORE
 *       Number()-coercion. This is deliberate: Number('') === 0, so without
 *       this guard an absent value would compare as zero (WEB-1268).
 *       Then Number()-coercion; NaN → FALSE (not throw).
 *   - String (equals/not_equals/contains/not_contains/begins_with/ends_with):
 *       case-insensitive + trim of both sides
 *   - is_one_of: trim+lower each; multi_select lhs → intersection ≥ 1
 *   - is_empty / is_not_empty: null|undefined|''(trim)|[] → empty
 *   - contains × multi_select array lhs: membership check per item (not string join)
 *   - Fail-open: unknown operator → FALSE (not throw)
 *
 * Pure module: no React/DOM/navigator/payment-SDK imports.
 */

import type { Operator } from './rule.schema';

/** Normalize string: trim + lowercase. */
function norm(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * WEB-1725: операторы, которые матрица ниже реально понимает для ответа-СПИСКА
 * (multi_select: ctx.answers[blockId] — массив). Все прочие (в том числе
 * equals/not_equals) на списке ложны ВСЕГДА — см. ветку default в разборе
 * массива. Перечень вынесен наружу как единственная точка правды: редактор
 * правил (components/editor/logic) строит по нему список операторов, чтобы
 * автор физически не мог собрать заведомо мёртвое условие.
 *
 * ВАЖНО: держать в согласии с case-ветками разбора массива в applyOperator —
 * согласие закреплено тестом operators.multiSelect.web1725.test.ts (он гоняет
 * весь operatorEnum и проверяет, что не входящие в перечень дают false).
 */
export const MULTI_SELECT_OPERATORS: readonly Operator[] = [
  'contains',
  'not_contains',
  'is_one_of',
  'is_empty',
  'is_not_empty',
];

/** true — оператор имеет смысл для ответа, приходящего списком. */
export function isOperatorSupportedForMultiSelect(operator: string): boolean {
  return (MULTI_SELECT_OPERATORS as readonly string[]).includes(operator);
}

/**
 * applyOperator — evaluate a single condition operator.
 *
 * @param operator - The operator name from operatorEnum
 * @param lhs      - Left-hand side: resolved from EvalContext source (string | string[] | null | undefined)
 * @param rhs      - Right-hand side: resolved from Condition.value (string | string[])
 * @returns boolean — true if condition matches, false otherwise (never throws)
 */
export function applyOperator(
  operator: string,
  lhs: unknown,
  rhs: unknown,
): boolean {
  // ---- Array LHS (multi_select) ------------------------------------------
  if (Array.isArray(lhs)) {
    // WEB-1725: единственная точка правды о «что умеет список» — перечень выше.
    // Ветка default ниже сохранена как страховка, но сюда мы уже не доходим для
    // неподдержанных операторов: они отсекаются одним предикатом, тем же, что
    // читает редактор правил.
    if (!isOperatorSupportedForMultiSelect(operator)) return false;
    switch (operator) {
      case 'contains': {
        const rhsNorm = norm(String(rhs ?? ''));
        return lhs.some((item) => norm(String(item)) === rhsNorm);
      }
      case 'is_one_of': {
        const rhsArr = Array.isArray(rhs) ? rhs : [rhs];
        const rhsSet = new Set(rhsArr.map((r) => norm(String(r ?? ''))));
        return lhs.some((item) => rhsSet.has(norm(String(item))));
      }
      case 'not_contains': {
        const rhsNorm = norm(String(rhs ?? ''));
        return !lhs.some((item) => norm(String(item)) === rhsNorm);
      }
      case 'is_empty':
        return lhs.length === 0;
      case 'is_not_empty':
        return lhs.length > 0;
      default:
        // equals / not_equals / numeric / begins_with / ends_with on multi_select = false
        // (spec: use contains/is_one_of for multi_select, not equals)
        return false;
    }
  }

  // ---- Scalar LHS (string | null | undefined) ----------------------------

  // is_empty / is_not_empty — checked before string coercion
  if (operator === 'is_empty') {
    if (lhs == null) return true;
    return String(lhs).trim() === '';
  }
  if (operator === 'is_not_empty') {
    if (lhs == null) return false;
    return String(lhs).trim() !== '';
  }

  // Coerce to string for remaining operators
  const lhsStr = lhs == null ? '' : String(lhs);

  switch (operator) {
    // String operators — case-insensitive + trim
    case 'equals':
      return norm(lhsStr) === norm(String(rhs ?? ''));
    case 'not_equals':
      return norm(lhsStr) !== norm(String(rhs ?? ''));
    case 'contains':
      return norm(lhsStr).includes(norm(String(rhs ?? '')));
    case 'not_contains':
      return !norm(lhsStr).includes(norm(String(rhs ?? '')));
    case 'begins_with':
      return norm(lhsStr).startsWith(norm(String(rhs ?? '')));
    case 'ends_with':
      return norm(lhsStr).endsWith(norm(String(rhs ?? '')));

    // Numeric operators — empty (either side) → FALSE before Number()-coercion,
    // then Number()-coercion; NaN → FALSE. See WEB-1268: Number('')===0, so the
    // empty-check MUST run first or an absent value silently compares as zero.
    case 'gt': {
      const rhsStr = rhs == null ? '' : String(rhs);
      if (lhsStr.trim() === '' || rhsStr.trim() === '') return false;
      const l = Number(lhsStr);
      const r = Number(rhsStr);
      if (isNaN(l) || isNaN(r)) return false;
      return l > r;
    }
    case 'gte': {
      const rhsStr = rhs == null ? '' : String(rhs);
      if (lhsStr.trim() === '' || rhsStr.trim() === '') return false;
      const l = Number(lhsStr);
      const r = Number(rhsStr);
      if (isNaN(l) || isNaN(r)) return false;
      return l >= r;
    }
    case 'lt': {
      const rhsStr = rhs == null ? '' : String(rhs);
      if (lhsStr.trim() === '' || rhsStr.trim() === '') return false;
      const l = Number(lhsStr);
      const r = Number(rhsStr);
      if (isNaN(l) || isNaN(r)) return false;
      return l < r;
    }
    case 'lte': {
      const rhsStr = rhs == null ? '' : String(rhs);
      if (lhsStr.trim() === '' || rhsStr.trim() === '') return false;
      const l = Number(lhsStr);
      const r = Number(rhsStr);
      if (isNaN(l) || isNaN(r)) return false;
      return l <= r;
    }

    // is_one_of — scalar: is lhs in the rhs list?
    case 'is_one_of': {
      const rhsArr = Array.isArray(rhs) ? rhs : [rhs];
      const lhsNorm = norm(lhsStr);
      return rhsArr.some((r) => norm(String(r ?? '')) === lhsNorm);
    }

    // Fail-open: unknown operator → false
    default:
      return false;
  }
}
